import * as $ from 'jquery';
import * as React from 'react';
import { BrowserEventName } from '../../gen-src/preslayer/Enums';
import { BrowserSupport } from '@tableau/browser-support';
import { EventInputType } from './EventInputType';
import { EventSource } from './EventSource';
import { MiscUtil } from '../utility/MiscUtil';
import { NormalizedEvent } from './NormalizedEvent';
import { TsConfig } from '../utility/VizclientUtils';

export type EventMap = { [eventName in BrowserEventName]?: EventSource };

/**
* Create and set <see cref="EventHandleSpec"/> object's properties with appropriate values.
*/
class NormalizedEventBuilder {
  // All events that we listen to
  private static handledEvents: EventMap = {};

  // string names of events that we listen to
  private static startEvents: EventMap = {};

  private static moveEvents: EventMap = {};
  private static endEvents: EventMap = {};
  private static outEvents: EventMap = {};
  private static scrollEvents: EventMap = {};
  private static cancelEvents: EventMap = {};
  private static initialized: boolean = false;

  public static get HandledEvents(): EventMap {
    NormalizedEventBuilder.SetupEventNames();
    return NormalizedEventBuilder.handledEvents;
  }

  public static CreateNormalizedEvent(
    evt: TouchEvent,
    listeningNode: HTMLElement,
    reactEvent: React.SyntheticEvent<HTMLDivElement> = null)
    : NormalizedEvent {
    NormalizedEventBuilder.SetupEventNames();
    let normalizedEvent = new NormalizedEvent(evt, listeningNode, reactEvent);
    normalizedEvent.source = NormalizedEventBuilder.HandledEvents[normalizedEvent.type];
    normalizedEvent.inputType = NormalizedEventBuilder.GetEventInputType(normalizedEvent.type);
    return normalizedEvent;
  }

  private static GetEventInputType(eventName: BrowserEventName)
    : EventInputType {
    NormalizedEventBuilder.SetupEventNames();
    let inputType: EventInputType = EventInputType.UnSupported;
    if (!MiscUtil.IsNullOrUndefined(NormalizedEventBuilder.startEvents[eventName])) {
      inputType = EventInputType.Start;
    } else if (!MiscUtil.IsNullOrUndefined(NormalizedEventBuilder.moveEvents[eventName])) {
      inputType = EventInputType.Move;
    } else if (!MiscUtil.IsNullOrUndefined(NormalizedEventBuilder.endEvents[eventName])) {
      inputType = EventInputType.End;
    } else if (!MiscUtil.IsNullOrUndefined(NormalizedEventBuilder.outEvents[eventName])) {
      inputType = EventInputType.Out;
    } else if (!MiscUtil.IsNullOrUndefined(NormalizedEventBuilder.cancelEvents[eventName])) {
      inputType = EventInputType.Cancel;
    } else if (!MiscUtil.IsNullOrUndefined(NormalizedEventBuilder.scrollEvents[eventName])) {
      inputType = EventInputType.Scroll;
    }
    return inputType;
  }

  public static Reset(): void {
    NormalizedEventBuilder.handledEvents = {};
    NormalizedEventBuilder.startEvents = {};
    NormalizedEventBuilder.moveEvents = {};
    NormalizedEventBuilder.endEvents = {};
    NormalizedEventBuilder.outEvents = {};
    NormalizedEventBuilder.scrollEvents = {};
    NormalizedEventBuilder.cancelEvents = {};
    NormalizedEventBuilder.initialized = false;
  }

  public static SetupEventNames(): void {
    if (NormalizedEventBuilder.initialized) { return; }
    // Always watch for touch events, whether web or mobile
    NormalizedEventBuilder.startEvents[BrowserEventName.TouchStart] = EventSource.Touch;
    NormalizedEventBuilder.moveEvents[BrowserEventName.TouchMove] = EventSource.Touch;
    NormalizedEventBuilder.endEvents[BrowserEventName.TouchEnd] = EventSource.Touch;
    NormalizedEventBuilder.cancelEvents[BrowserEventName.TouchCancel] = EventSource.Touch;

    // if doing development with ?:mobile=true, mouse masquerades as touch so watch for subset
    // of mouse events that masquerade as touch. BUGZID 98802
    if (TsConfig.IsMobile()) {
      if (!BrowserSupport.IsTouch()) {
        NormalizedEventBuilder.startEvents[BrowserEventName.MouseDown] = EventSource.Mouse;
        NormalizedEventBuilder.moveEvents[BrowserEventName.MouseMove] = EventSource.Mouse;
        NormalizedEventBuilder.endEvents[BrowserEventName.MouseUp] = EventSource.Mouse;
        NormalizedEventBuilder.outEvents[BrowserEventName.MouseOut] = EventSource.Mouse;
      }
      // else if in normal mobile environment, ignore mouse events. BUGZID 97746
    } else {
      // else if web, watch for mouse events
      NormalizedEventBuilder.startEvents[BrowserEventName.MouseDown] = EventSource.Mouse;
      NormalizedEventBuilder.moveEvents[BrowserEventName.MouseMove] = EventSource.Mouse;
      NormalizedEventBuilder.endEvents[BrowserEventName.MouseUp] = EventSource.Mouse;
      NormalizedEventBuilder.outEvents[BrowserEventName.MouseOut] = EventSource.Mouse;
      NormalizedEventBuilder.scrollEvents[BrowserEventName.Scroll] = EventSource.Mouse;
      NormalizedEventBuilder.endEvents[BrowserEventName.ContextMenu] = EventSource.Mouse;
    }
    $.extend(
      NormalizedEventBuilder.handledEvents,
      NormalizedEventBuilder.startEvents,
      NormalizedEventBuilder.moveEvents,
      NormalizedEventBuilder.endEvents,
      NormalizedEventBuilder.outEvents,
      NormalizedEventBuilder.scrollEvents,
      NormalizedEventBuilder.cancelEvents);

    NormalizedEventBuilder.initialized = true;
  }
}

export { NormalizedEventBuilder };
